import { makeAutoObservable, runInAction } from "mobx";
import { track } from "@/lib/analytics";
import { peekQueryClient } from "@/queries/client";
import { patchScoped } from "@/queries/invalidation";
import { myInvitationsKey } from "@/queries/invitations";
import { GLOBAL_TENANT } from "@/queries/scope";
import { queryCacheScope } from "./query-cache-scope";
import type { RootStore } from "./root.store";
import type { IWorkspaceMemberInvitation } from "@/types/workspace";

/**
 * Which of the signed-in user's invitations are ticked on `/invitations`, and
 * accepting them. The invitations themselves are read only through Query
 * (`useMyPendingInvitations`, NG-2814).
 */
export class InvitationStore {
  selectedIds: Set<string> = new Set();

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  toggleSelection(id: string): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  clearSelection(): void {
    this.selectedIds.clear();
  }

  /**
   * Accept the ticked invitations out of `shown` (what the page lists) and
   * answer the workspace to go to: the first accepted one's, or `null`.
   */
  async acceptSelected(shown: readonly IWorkspaceMemberInvitation[]): Promise<string | null> {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0) return null;

    await this.rootStore.invitationService.acceptInvitations({ invitations: ids });
    track("workspace_member_joined", { count: ids.length });

    const firstAccepted = shown.find((inv) => ids.includes(inv.id));
    const redirectSlug = firstAccepted?.workspace.slug ?? null;

    const scope = queryCacheScope(GLOBAL_TENANT, "invitation");
    const client = peekQueryClient();
    if (scope && client) {
      patchScoped<IWorkspaceMemberInvitation[]>(client, myInvitationsKey(scope), (current) =>
        current.filter((inv) => !ids.includes(inv.id)),
      );
    }
    runInAction(() => {
      this.selectedIds.clear();
    });

    await this.rootStore.workspace.fetchWorkspaces();

    return redirectSlug;
  }
}
